import React from 'react'
import {GROUP_LABELS, MAX_DAILY_DAYS} from '../constants'
import {closingTime, detailLabel, fmDate, fmN, fmRange} from '../helpers/format'
import {dailyDimSeries, dayCats, groupKeys, groupSeries, hasDailyOutput, monthCats, monthlyDimSeries, shareSlices} from '../helpers/series'
import StackedBarLine from './charts/StackedBarLine'
import Pareto, {paretoK80} from './charts/Pareto'
import Legend from './charts/Legend'
import SharePie from './charts/SharePie'
import InsightBlock from './sections/InsightBlock'
import NotesBlock from './sections/NotesBlock'
import MetricTable from './sections/MetricTable'

/**
 * Bố cục một tab công đoạn, port từ renderDim() của bản mẫu:
 * xu hướng tháng → chi tiết ngày → Pareto → bảng → nhận định.
 * tab: phần tử của TABS; byGroup = true khi chiều là nhóm lỗi (tab Process)
 */
// "3/12 máy in đã chiếm 80% số lỗi"; chưa đạt 80% trong các mục đang có thì nói rõ tỉ trọng
const k80Text = ({count, reached, share}, total, unit) => reached
    ? `${count}/${total} ${unit} đã chiếm 80% số lỗi`
    : `${count} ${unit} đầu (trong ${total}) mới chiếm ${Math.round(share * 100)}% số lỗi`

const TabView = ({tab, data, byGroup = false}) => {
    const unit = tab.unit
    const unitLower = unit.toLowerCase()
    const stageGroup = byGroup
        ? (data.filters.applied.groups && data.filters.applied.groups.length === 1 ? data.filters.applied.groups[0] : null)
        : tab.group
    const keys = groupKeys(data.filters.applied.groups)

    const months = data.trend_monthly
    const monthSeries = byGroup ? groupSeries(months, keys) : monthlyDimSeries(months, data.dim_names)
    const days = data.daily
    const showDays = days.length <= MAX_DAILY_DAYS
    const dayPct = hasDailyOutput(days)
    const daySeries = byGroup ? groupSeries(days, keys, dayPct) : dailyDimSeries(days, data.table, data.dim_names, dayPct)
    const mutedDays = days.map((day, index) => day.closed === false ? index : null).filter(index => index !== null)

    const paretoByUnit = data.table.filter(row => row.hits > 0).map(row => ({label: row.dim_name || row.dim_id, value: row.hits}))
    const paretoByDetail = data.pareto.map(entry => ({label: detailLabel(entry), value: entry.hits}))
    const k80Unit = paretoK80(paretoByUnit, data.summary.hits)
    const k80Detail = paretoK80(paretoByDetail, data.summary.hits)

    return (
        <>
            <h2>{tab.label}</h2>
            <p className="sub2">
                {stageGroup && <>Chỉ tính <b>{GROUP_LABELS[stageGroup]}</b> — </>}
                theo {unitLower} · tính theo {data.date_basis}
            </p>

            <div className="sheet">
                <div className="chart-t">NG rate monitoring — {tab.label}</div>
                <div className="chart-n">Xu hướng theo tháng, tính trên toàn kỳ (không phụ thuộc bộ lọc ngày)</div>
                <StackedBarLine cats={monthCats(months)} series={monthSeries} aria="xu hướng tháng" xLabel="tháng"/>
                <Legend labels={monthSeries.map(s => s.label)} colors={monthSeries.map(s => s.color)}/>
            </div>

            <div className="sheet">
                <div className="chart-t">Chi tiết theo ngày</div>
                <div className="chart-n">
                    {showDays ? `${days.length} ngày trong khoảng đã chọn` : `Khoảng quá dài để vẽ theo ngày — thu hẹp dưới ${MAX_DAILY_DAYS} ngày`}
                </div>
                {showDays ? (
                    <>
                        <StackedBarLine
                            cats={dayCats(days)} series={daySeries} pct={dayPct} aria="chi tiết ngày"
                            xLabel={dayPct ? 'ngày' : 'ngày — số lỗi (khoảng này chưa có sản lượng theo ngày)'}
                            mutedIndexes={mutedDays}
                            tipExtra={(index) => days[index].closed === false
                                ? <div><span className="k">Chưa chốt sổ</span> <b>chốt lúc {closingTime(days[index].date)}</b></div>
                                : <div><span className="k">Ngày</span> <b>{fmDate(days[index].date)}</b></div>}
                        />
                        <Legend labels={daySeries.map(s => s.label)} colors={daySeries.map(s => s.color)}/>
                    </>
                ) : (
                    <div className="empty">Chọn khoảng ngắn hơn {MAX_DAILY_DAYS} ngày để xem chi tiết theo ngày.</div>
                )}
            </div>

            <div className="sheet">
                <div className="chart-t">Pareto — lỗi tập trung ở đâu</div>
                <div className="chart-n">Cột: tỉ trọng từng loại · đường: tích luỹ · ngưỡng 80% là mốc ưu tiên xử lý</div>
                <div className="g2">
                    <div><div className="chart-t2">Theo {unitLower}</div><Pareto items={paretoByUnit} total={data.summary.hits}/></div>
                    <div><div className="chart-t2">Theo chi tiết lỗi</div><Pareto items={paretoByDetail} total={data.summary.hits}/></div>
                </div>
                {!!data.summary.hits && (
                    <div className="note">
                        <b>Đọc Pareto:</b> {k80Text(k80Unit, paretoByUnit.length, unitLower)};
                        {' '}{k80Text(k80Detail, data.pareto_detail_count, 'loại lỗi chi tiết')}. Đây là danh sách ưu tiên xử lý trong khoảng đang xem.
                    </div>
                )}
            </div>

            <div className="sheet">
                {/* Ghi rõ khoảng ngày: khối này hay bị chụp gửi đi, ảnh không có thanh lọc */}
                <div className="blk">Số lỗi và tỉ lệ · {fmRange(data.range) || 'trong khoảng đã chọn'}</div>
                <div className="tbl-pie">
                    <MetricTable unit={unit} table={data.table} summary={data.summary}/>
                    <div>
                        <div className="chart-t2">Cơ cấu lỗi theo {unitLower}</div>
                        <div className="chart-n">Tỉ trọng trên {fmN(data.summary.hits)} lượt ghi nhận · không phải tỉ lệ lỗi</div>
                        <SharePie slices={shareSlices(data.table, {byGroup, keys})} aria={`cơ cấu lỗi theo ${unitLower}`}/>
                    </div>
                </div>
                <NotesBlock notes={data.notes} warnings={data.warnings} pendingSources={data.pending_sources} outputBasis={data.output_basis}/>
            </div>

            <InsightBlock lines={data.insights}/>
        </>
    )
}

export default TabView
